import React, {
    Dispatch,
    SetStateAction,
    SyntheticEvent,
    useEffect,
    useRef,
} from 'react';
import { gql, useMutation } from '@apollo/client';
import { uuid } from 'uuidv4';

import { Button, FileDropZone, IconUploadCloud } from '@trqk/components';
import {
    locale,
    getS3KeyFromUrl,
    getWebpackEnvVar,
    getFileInputAccept,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { useGetTrqkUserAvatarLazyQuery } from '@trqk/api';

import { useTasks, useToast } from '../../../../../hooks';
import { getAvatarKey } from './helpers';

const { DELETE_OBJECTS_IN_S3, TASK_RESIZE_AND_UPLOAD_IMAGE } = queryStrings;

const UPDATE_TRQK_USER = gql`
    mutation updateTrqkUser($input: UpdateTrqkUserInput!) {
        updateTrqkUser(input: $input) {
            id
            avatar_url
        }
    }
`;

const DropZoneContent = (): JSX.Element => (
    <>
        <div className="flex-initial">
            <IconUploadCloud fillColor="white" width={40} />
        </div>
        <div className="flex-initial text-center text-white">
            {locale.enUS.ACCOUNT.MODALS.CHANGE_AVATAR_IMAGE.DROPZONE}
        </div>
    </>
);

const fileTypeKeys: FileTypeKey[] = ['jpg', 'png'];
const fileInputAccept = getFileInputAccept(fileTypeKeys);

const taskGroupName = 'resize-and-upload-avatar-image';
const imageMaxSize = 500;

/**
 * @typedef {ChangeAvatarImageModalInput}
 * @property {function} setImageHash a state update function to set a timestamp so that the browser
 * knows to re-fetch the image
 * @property {function} setLoading a state update function to set the loading state
 * @property {Maybe<string>} userId the current user ID
 */
interface ChangeAvatarImageModalInput {
    setImageHash: Dispatch<SetStateAction<number>>;
    setLoading: Dispatch<SetStateAction<boolean>>;
    userId: Maybe<string>;
}

/**
 * A modal that allows the user to update their avatar image by uploading a new file or deleting
 * the existing image.
 * @param props {ModalContentProps<ChangeAvatarImageModalInput>}
 */
export const ChangeAvatarImageModal = ({
    remove,
    input,
}: ModalContentProps<ChangeAvatarImageModalInput>): JSX.Element => {
    const bucketName = getWebpackEnvVar('web', 'AWS_S3_USER_IMAGES_BUCKET');
    const bucketRegion = getWebpackEnvVar('web', 'AWS_REGION');
    const identityPoolId = getWebpackEnvVar('web', 'AWS_IDENTITY_POOL_ID');
    const userId = input?.userId ?? null;
    const setLoading = input?.setLoading ?? null;
    const setImageHash = input?.setImageHash ?? null;
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const toast = useToast();
    const { sendTaskEvent } = useTasks();
    const [getTrqkUser, getTrqkUserQuery] = useGetTrqkUserAvatarLazyQuery();

    useEffect(() => {
        if (userId) {
            getTrqkUser({ variables: { id: userId } });
        }
    }, [userId]);

    const avatarUrl = getTrqkUserQuery?.data?.getTrqkUser?.avatar_url ?? null;

    const [updateTrqkUser] = useMutation(UPDATE_TRQK_USER, {
        onCompleted: data => {
            const updatedAvatarUrl = data?.updateTrqkUser?.avatar_url ?? null;

            if (setImageHash !== null) {
                setImageHash(Date.now());
            }

            // If the updatedAvatarUrl is null, we need to proceed with removing the modal because
            // that hasn't been done yet
            if (updatedAvatarUrl === null) {
                remove({
                    canceled: false,
                    success: true,
                });
            }
        },
        onError: () => {
            // The modal will only be open if this failure was due to an issue with setting
            // `avatar_url: null` on the user
            toast.add({
                kind: 'warning-banner',
                content: locale.enUS.ACCOUNT.TOASTS.REMOVE_AVATAR_IMAGE_FAILED,
            });

            remove({
                canceled: false,
                success: false,
            });
        },
    });

    const [deleteObjectsInS3] = useMutation(DELETE_OBJECTS_IN_S3);

    const [taskResizeAndUploadImage] = useMutation(
        TASK_RESIZE_AND_UPLOAD_IMAGE,
    );

    const handleFiles = (files: File[]): void => {
        if (files.length > 1) {
            toast.add({
                kind: 'warning-banner',
                content: locale.enUS.ACCOUNT.TOASTS.UPLOAD_ONE_IMAGE_AT_A_TIME,
            });
        } else {
            const file = files[0];

            if (file) {
                const key = getAvatarKey(userId, file);
                const groupId = uuid();
                const taskId = uuid();

                const taskCreators = [
                    {
                        taskId,
                        input: {
                            filename: file.name,
                        },
                        onTaskStart: (): void => {
                            taskResizeAndUploadImage({
                                context: {
                                    sendTaskEvent,
                                },
                                variables: {
                                    bucketName,
                                    bucketRegion,
                                    file,
                                    identityPoolId,
                                    key,
                                    maxSize: imageMaxSize,
                                    taskId,
                                },
                            });
                        },
                    },
                ];

                sendTaskEvent({
                    type: 'CREATE_GROUP',
                    value: {
                        groupId,
                        groupName: taskGroupName,
                        taskCreators,
                        onTaskUpdate: (
                            task: Task<FilenameInput, UploadToS3TaskOutput>,
                        ): void => {
                            const { canceled, done, error, success } = task;

                            if (setLoading !== null) {
                                if (!canceled && !error && !done && !success) {
                                    setLoading(true);
                                } else {
                                    setLoading(false);
                                }
                            }
                        },
                        onGroupStopped: (
                            onGroupStoppedInput: OnGroupStoppedInput<
                                FilenameInput,
                                UploadToS3TaskOutput
                            >,
                        ): void => {
                            const { error, success } = onGroupStoppedInput;

                            if (error.length > 0) {
                                toast.add({
                                    kind: 'warning-banner',
                                    content:
                                        locale.enUS.ACCOUNT.TOASTS
                                            .UPLOAD_AVATAR_IMAGE_FAILED,
                                });
                            }

                            if (success.length > 0) {
                                const taskResult = success[0];
                                const location =
                                    taskResult?.output?.location ?? null;

                                if (location !== null) {
                                    updateTrqkUser({
                                        variables: {
                                            input: {
                                                id: userId,
                                                avatar_url: location,
                                            },
                                        },
                                    });
                                }
                            }
                        },
                    },
                });

                // Remove the modal right away when uploading
                remove({
                    canceled: false,
                    success: true,
                });
            }
        }
    };

    return (
        <FileDropZone
            acceptFileTypes={fileTypeKeys}
            handleDrop={({
                validFiles,
                invalidFiles,
            }: HandleDropInput): void => {
                if (invalidFiles.length > 0) {
                    toast.add({
                        kind: 'warning-banner',
                        content: locale.enUS.DROPPED_WRONG_FILE_TYPE.IMAGES,
                    });
                } else {
                    handleFiles(validFiles);
                }
            }}
            overlayClasses="rounded-lg"
            DropZoneContent={DropZoneContent}
        >
            <div
                className="px-12 py-10 bg-white rounded-lg border-l-4 border-primary-500"
                style={{ width: 500 }}
            >
                <h4>{locale.enUS.ACCOUNT.MODALS.CHANGE_AVATAR_IMAGE.TITLE}</h4>
                <div className="mt-4 leading-8">
                    {locale.enUS.ACCOUNT.MODALS.CHANGE_AVATAR_IMAGE.CTA.PART_1}
                    &nbsp;
                    <button
                        type="button"
                        className="text-primary-500 underline respond-to-mouse"
                        onClick={(): void => {
                            if (fileInputRef.current) {
                                fileInputRef.current.click();
                            }
                        }}
                    >
                        {
                            locale.enUS.ACCOUNT.MODALS.CHANGE_AVATAR_IMAGE.CTA
                                .PART_2
                        }
                    </button>
                    <input
                        accept={fileInputAccept}
                        className="visually-hidden"
                        id="change-avatar-image-input"
                        onChange={(e: SyntheticEvent): void => {
                            e.preventDefault();

                            if (fileInputRef.current) {
                                const files = Array.from(
                                    fileInputRef.current?.files ?? [],
                                );

                                handleFiles(files);

                                // Reset the input's current value so it can be used again.
                                fileInputRef.current.value = '';
                            }
                        }}
                        ref={fileInputRef}
                        type="file"
                    />
                </div>
                <div className="mt-6 flex justify-end">
                    <Button
                        color="danger-tertiary-light-bg"
                        autoWidth={true}
                        addClasses="respond-to-mouse"
                        handleClick={(): void => {
                            remove({
                                canceled: true,
                                success: true,
                            });
                        }}
                    >
                        <div className="mx-3">
                            {
                                locale.enUS.ACCOUNT.MODALS.CHANGE_AVATAR_IMAGE
                                    .BUTTONS.CANCEL
                            }
                        </div>
                    </Button>
                    <Button
                        color="danger-secondary-light-bg"
                        disabled={avatarUrl === null}
                        autoWidth={true}
                        addClasses="respond-to-mouse"
                        handleClick={(): void => {
                            if (avatarUrl !== null) {
                                const Key = getS3KeyFromUrl(avatarUrl);

                                if (Key) {
                                    deleteObjectsInS3({
                                        variables: {
                                            bucketName,
                                            bucketRegion,
                                            identityPoolId,
                                            objects: [
                                                {
                                                    Key,
                                                },
                                            ],
                                        },
                                    });
                                }
                            }

                            // We don't mind if the avatar image deletion fails; we still want to
                            // set the user's `avatar_url` to `null`
                            if (userId !== null) {
                                updateTrqkUser({
                                    variables: {
                                        input: {
                                            id: userId,
                                            avatar_url: null,
                                        },
                                    },
                                });
                            }
                        }}
                    >
                        <div className="mx-3">
                            {
                                locale.enUS.ACCOUNT.MODALS.CHANGE_AVATAR_IMAGE
                                    .BUTTONS.REMOVE_IMAGE
                            }
                        </div>
                    </Button>
                </div>
            </div>
        </FileDropZone>
    );
};
